import type { CommonRole, Roles } from 'modules/account/services/useGetRoles';

export interface UserRoleFlags {
  isRAD: boolean;
  isSAD: boolean;
  isCSBAD: boolean;
}

/** Org type from API: system | client | govAgency */
export type OrgTypeApi = 'system' | 'client' | 'govAgency';

/**
 * Code của 5 vai trò nghiệp vụ — khớp cột `roles.code` set trong roles_setup.sql (id cố định
 * mọi env). Lọc theo code (không theo tên/UUID) cho bền, giống pattern useUserRoles.
 */
export const ROLE_CODE = {
  bigClient: 'BIG_CLIENT', // Nhân sự kế toán - Quản lý KH (725026df)
  nutriClient: 'NUTRI_CLIENT', // Chuyên gia dinh dưỡng KH (b6ef8ee5)
  parent: 'PARENT', // Phụ huynh (7738cc55)
  bigGov: 'BIG_GOV', // Nhân sự kế toán - Quản lý CQQL (cecf8ca3)
  nutriGov: 'NUTRI_GOV', // Chuyên gia dinh dưỡng CQQL (891e7b1e)
} as const;

/**
 * Chức vụ (jobTitle id) KHÁCH HÀNG → tập role code được phép gán ở tab Vai trò.
 * Nguồn chuẩn: xlsx 28.07 sheet "01- VT Người dùng" (ô 'x'; 'Bỏ' = loại role đó).
 * CQQL/HVS KHÔNG lọc theo chức vụ (mọi chức vụ CQQL đều thấy đủ 2 role gov theo xlsx).
 */
const CLIENT_JOBTITLE_ROLE_CODES: Record<string, string[]> = {
  'KH-KTOAN': [ROLE_CODE.bigClient, ROLE_CODE.nutriClient, ROLE_CODE.parent],
  'KH-LDAO': [ROLE_CODE.bigClient, ROLE_CODE.nutriClient, ROLE_CODE.parent],
  'KH-QLSAN': [ROLE_CODE.bigClient, ROLE_CODE.nutriClient, ROLE_CODE.parent],
  'KH-NVIEN': [ROLE_CODE.nutriClient, ROLE_CODE.parent], // loại "Nhân sự kế toán - Quản lý KH"
  'KH-PHOTHONG': [ROLE_CODE.parent], // chỉ Phụ huynh
};

/**
 * Lọc danh sách vai trò nghiệp vụ theo CHỨC VỤ của tài khoản đang tạo/sửa.
 * Chỉ áp dụng cho KHÁCH HÀNG; gov/khác giữ nguyên. Chức vụ lạ (không có trong map) → giữ
 * nguyên để tránh ẩn sạch tab (an toàn hơn là ẩn hết).
 */
function filterClientRolesByJobTitle(
  businessRoles: CommonRole[] | undefined,
  orgType: OrgTypeApi,
  jobTitleId?: string
): CommonRole[] {
  const list = businessRoles ?? [];
  if (orgType !== 'client' || !jobTitleId) return list;
  const allowed = CLIENT_JOBTITLE_ROLE_CODES[jobTitleId];
  if (!allowed) return list;
  return list.filter((r) => typeof r.code === 'string' && allowed.includes(r.code));
}

/**
 * Returns which role groups the current user can assign, based on their role and target user's org.
 * Nếu truyền `jobTitleId` (client), vai trò nghiệp vụ còn được lọc theo chức vụ tài khoản đích.
 * Used by RolesList (detail) and StepThree (create).
 */
export function getAvailableRolesForUser(
  userRoles: UserRoleFlags,
  orgType: OrgTypeApi,
  roles: Partial<Roles>,
  jobTitleId?: string
): Partial<Roles> {
  if (!roles) return {};

  const { isRAD, isSAD, isCSBAD } = userRoles;
  const businessRoles = filterClientRolesByJobTitle(
    roles.businessRoles as CommonRole[] | undefined,
    orgType,
    jobTitleId
  );

  if (isRAD) return { systemRoles: roles.systemRoles || [] };
  if (isSAD) {
    if (orgType === 'system') return { adminRoles: roles.adminRoles || [] };
    return { businessRoles };
  }
  if (isCSBAD && orgType === 'client') {
    return { businessRoles };
  }

  return {};
}
